/**
 * Voice Room Settings Modal Component
 * Modal for editing voice room settings and deleting rooms (admin only)
 */

import { useState } from 'react';
import { useUpdateVoiceRoom, useDeleteVoiceRoom } from '@/hooks/useVoiceRooms';
import { useDialogA11y } from '@/hooks/useDialogA11y';
import type { VoiceRoom } from '@/types/voice-session.types';

interface VoiceRoomSettingsModalProps {
  voiceRoom: VoiceRoom;
  onClose: () => void;
}

export const VoiceRoomSettingsModal = ({
  voiceRoom,
  onClose,
}: VoiceRoomSettingsModalProps) => {
  const [name, setName] = useState(voiceRoom.name);
  const [description, setDescription] = useState(voiceRoom.description || '');
  const [maxParticipants, setMaxParticipants] = useState(voiceRoom.maxParticipants);
  const [position, setPosition] = useState(voiceRoom.position);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { dialogRef, titleId } = useDialogA11y(onClose);

  const updateMutation = useUpdateVoiceRoom();
  const deleteMutation = useDeleteVoiceRoom();

  // Sync local form state from the voiceRoom prop (adjust state during render on change).
  const [syncedVoiceRoom, setSyncedVoiceRoom] = useState(voiceRoom);
  if (voiceRoom !== syncedVoiceRoom) {
    setSyncedVoiceRoom(voiceRoom);
    setName(voiceRoom.name);
    setDescription(voiceRoom.description || '');
    setMaxParticipants(voiceRoom.maxParticipants);
    setPosition(voiceRoom.position);
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Името на стаята е задължително');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        roomId: voiceRoom.id,
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
          maxParticipants,
          position,
        },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Грешка при актуализиране на стая');
    }
  };

  const handleArchive = async () => {
    try {
      await updateMutation.mutateAsync({
        roomId: voiceRoom.id,
        data: { isArchived: !voiceRoom.isArchived },
      });
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Грешка при ${voiceRoom.isArchived ? 'възстановяване' : 'архивиране'} на стая`
      );
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(voiceRoom.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Грешка при изтриване на стая');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50" onClick={onClose}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="bg-white dark:bg-dark-bg-elevated rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border-primary sticky top-0 bg-white dark:bg-dark-bg-elevated">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
            Настройки на стая
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 dark:text-dark-text-muted hover:text-gray-600 dark:hover:text-dark-text-secondary rounded transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Basic Settings Form */}
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
                Основни настройки
              </h3>

              {/* Name Input */}
              <div className="mb-4">
                <label
                  htmlFor="room-name"
                  className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1"
                >
                  Име на стаята *
                </label>
                <input
                  id="room-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input"
                  placeholder="например: Общ офис, Среща"
                  maxLength={100}
                  disabled={voiceRoom.isArchived}
                />
              </div>

              {/* Description Input */}
              <div className="mb-4">
                <label
                  htmlFor="room-description"
                  className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1"
                >
                  Описание
                </label>
                <textarea
                  id="room-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="input resize-none"
                  rows={3}
                  placeholder="Кратко описание на стаята..."
                  maxLength={500}
                  disabled={voiceRoom.isArchived}
                />
              </div>

              {/* Max Participants */}
              <div className="mb-4">
                <label
                  htmlFor="room-max-participants"
                  className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1"
                >
                  Максимален брой участници
                </label>
                <input
                  id="room-max-participants"
                  type="number"
                  min={2}
                  max={100}
                  value={maxParticipants}
                  onChange={(e) => setMaxParticipants(Math.max(2, Math.min(100, parseInt(e.target.value) || 2)))}
                  className="input w-32"
                  disabled={voiceRoom.isArchived}
                />
                <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
                  Между 2 и 100 участника
                </p>
              </div>

              {/* Position Input */}
              <div>
                <label
                  htmlFor="room-position"
                  className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1"
                >
                  Позиция (ред на показване)
                </label>
                <input
                  id="room-position"
                  type="number"
                  min={0}
                  value={position}
                  onChange={(e) => setPosition(Math.max(0, parseInt(e.target.value) || 0))}
                  className="input w-32"
                  disabled={voiceRoom.isArchived}
                />
                <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
                  По-малък номер = по-нагоре в списъка
                </p>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            {/* Update Button */}
            {!voiceRoom.isArchived && (
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? 'Запазване...' : 'Запази промените'}
                </button>
              </div>
            )}
          </form>

          {/* Danger Zone */}
          <div className="border-t border-gray-200 dark:border-dark-border-primary pt-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary mb-3">
              Опасна зона
            </h3>

            <div className="space-y-3">
              {/* Archive/Unarchive Button */}
              <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                    {voiceRoom.isArchived ? 'Възстанови стаята' : 'Архивирай стаята'}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {voiceRoom.isArchived
                      ? 'Стаята ще стане отново активна и видима'
                      : 'Стаята ще бъде скрита от списъка'}
                  </div>
                </div>
                <button
                  onClick={handleArchive}
                  className="btn-secondary text-yellow-700 hover:bg-yellow-100 ml-4"
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending
                    ? 'Обработка...'
                    : voiceRoom.isArchived
                    ? 'Възстанови'
                    : 'Архивирай'}
                </button>
              </div>

              {/* Delete Button */}
              {!showDeleteConfirm ? (
                <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                      Изтрий стаята
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      Това действие е необратимо. Всички сесии ще бъдат прекратени.
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="btn-secondary text-red-700 hover:bg-red-100 ml-4"
                  >
                    Изтрий
                  </button>
                </div>
              ) : (
                <div className="p-4 bg-red-100 border-2 border-red-300 rounded-lg">
                  <p className="text-sm font-semibold text-red-900 mb-2">
                    Сигурни ли сте?
                  </p>
                  <p className="text-xs text-red-800 mb-3">
                    Това действие е необратимо. Стаята и всички свързани сесии
                    ще бъдат изтрити завинаги.
                  </p>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleDelete}
                      className="btn-primary bg-red-600 hover:bg-red-700 text-white"
                      disabled={deleteMutation.isPending}
                    >
                      {deleteMutation.isPending ? 'Изтриване...' : 'Да, изтрий стаята'}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="btn-secondary"
                    >
                      Отказ
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-gray-200 dark:border-dark-border-primary bg-gray-50 dark:bg-dark-bg-secondary">
          <button onClick={onClose} className="btn-secondary">
            Затвори
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoiceRoomSettingsModal;
